<template id="app-header">
  <div class="app-header" :class="{ 'menu-open': mobileMenuOpen }">
    <header class="fw-header">
      <div class="fw-header__bar">
        <!-- Width locked to AppMenu baseMenuWidth (230px); does not follow menu resize -->
        <a class="fw-brand" href="#" @click.prevent>
          <img class="fw-brand__logo" :src="logoPath" alt="Logo" />
          <span class="fw-brand__powered">
            Powered by
            <a href="https://compegence.com/products" target="_blank" rel="noopener">
              <i class="fore">Fore</i><i class="warn">Warn</i>
            </a>
          </span>
        </a>

        <!-- Desktop actions -->
        <nav class="fw-nav d-none d-lg-flex" aria-label="Header navigation" @click.stop>
          <router-link class="fw-nav__btn fw-nav__btn--icon" :to="homeIconUrl" v-b-tooltip.hover title="Home">
            <i class="fa fa-home"></i>
          </router-link>

          <router-link class="fw-nav__btn fw-nav__btn--icon fw-nav__btn--badge" to="/annotation-messages/Parent/C"
                       v-b-tooltip.hover title="Messages">
            <i class="fas fa-envelope"></i>
            <span class="fw-badge" v-if="$store.state.annotationCount">{{ $store.state.annotationCount }}</span>
          </router-link>

          <div class="fw-nav__menu" :class="{ open: desktopMenu === 'user' }">
            <button
                type="button"
                class="fw-nav__btn fw-nav__btn--text"
                :aria-expanded="desktopMenu === 'user' ? 'true' : 'false'"
                @click="toggleDesktopMenu('user')"
            >
              <span class="fw-nav__user-label">{{ $store.state.firstName }}</span>
              <i class="fa fa-caret-down fw-nav__caret"></i>
            </button>
            <div v-show="desktopMenu === 'user'" class="fw-nav__menu-panel">
              <router-link class="fw-nav__menu-item" to="/change-password" @click.native="closeDesktopMenu">
                <i class="fa fa-key"></i>Change Password
              </router-link>
            </div>
          </div>

          <div class="fw-nav__btn fw-nav__btn--pill" v-b-tooltip.hover title="Current Role">
            <i class="fa fa-users"></i>
            <span>{{ $store.state.roleName }}</span>
          </div>

          <div class="fw-nav__menu" :class="{ open: desktopMenu === 'help' }">
            <button
                type="button"
                class="fw-nav__btn fw-nav__btn--text"
                :aria-expanded="desktopMenu === 'help' ? 'true' : 'false'"
                @click="toggleDesktopMenu('help')"
            >
              <span>Help</span>
              <i class="fa fa-caret-down fw-nav__caret"></i>
            </button>
            <div v-show="desktopMenu === 'help'" class="fw-nav__menu-panel">
              <router-link class="fw-nav__menu-item" to="/about" @click.native="closeDesktopMenu">
                <i class="fa fa-info-circle"></i>About ForeWarn
              </router-link>
              <router-link class="fw-nav__menu-item" to="/release-notes" @click.native="closeDesktopMenu">
                <i class="fa fa-file-alt"></i>Latest Release Note
              </router-link>
            </div>
          </div>

          <button
              type="button"
              class="fw-nav__btn fw-nav__btn--icon"
              v-b-tooltip.hover
              title="Collapse / Expand Banner"
              @click="toggleBanner"
          >
            <i class="fa" :class="showBanner ? 'fa-toggle-off' : 'fa-toggle-on'"></i>
          </button>

          <button
              type="button"
              class="fw-nav__btn fw-nav__btn--icon fw-nav__btn--danger"
              v-b-tooltip.hover
              title="Sign Out"
              @click="logout"
          >
            <i class="fa fa-sign-out-alt"></i>
          </button>
        </nav>

        <!-- Mobile hamburger -->
        <button
            type="button"
            class="fw-burger d-lg-none"
            :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
            aria-controls="fw-mobile-panel"
            aria-label="Open menu"
            @click="toggleMobileMenu"
        >
          <span class="fw-burger__line" :class="{ open: mobileMenuOpen }"></span>
          <span class="fw-burger__line" :class="{ open: mobileMenuOpen }"></span>
          <span class="fw-burger__line" :class="{ open: mobileMenuOpen }"></span>
        </button>
      </div>

      <!-- Mobile panel -->
      <transition name="fw-panel">
        <div
            v-show="mobileMenuOpen"
            id="fw-mobile-panel"
            class="fw-mobile-panel d-lg-none"
            role="dialog"
            aria-modal="true"
            aria-label="Account menu"
        >
          <div class="fw-mobile-panel__user">
            <div class="fw-avatar">{{ userInitials }}</div>
            <div class="fw-mobile-panel__meta">
              <div class="fw-mobile-panel__name">{{ $store.state.firstName || 'User' }}</div>
              <div class="fw-mobile-panel__role">
                <i class="fa fa-users"></i>
                {{ $store.state.roleName || 'Role' }}
              </div>
            </div>
          </div>

          <div class="fw-mobile-panel__actions">
            <router-link class="fw-action" :to="homeIconUrl" @click.native="closeMobileMenu">
              <span class="fw-action__icon"><i class="fa fa-home"></i></span>
              <span class="fw-action__label">Home</span>
              <i class="fa fa-chevron-right fw-action__chevron"></i>
            </router-link>

            <router-link class="fw-action" to="/annotation-messages/Parent/C" @click.native="closeMobileMenu">
              <span class="fw-action__icon"><i class="fas fa-envelope"></i></span>
              <span class="fw-action__label">Messages</span>
              <span class="fw-badge fw-badge--inline" v-if="$store.state.annotationCount">
                {{ $store.state.annotationCount }}
              </span>
              <i class="fa fa-chevron-right fw-action__chevron"></i>
            </router-link>

            <router-link class="fw-action" to="/change-password" @click.native="closeMobileMenu">
              <span class="fw-action__icon"><i class="fa fa-key"></i></span>
              <span class="fw-action__label">Change Password</span>
              <i class="fa fa-chevron-right fw-action__chevron"></i>
            </router-link>

            <button type="button" class="fw-action" @click="toggleBannerFromMobile">
              <span class="fw-action__icon">
                <i class="fa" :class="showBanner ? 'fa-toggle-off' : 'fa-toggle-on'"></i>
              </span>
              <span class="fw-action__label">{{ showBanner ? 'Hide Banner' : 'Show Banner' }}</span>
            </button>

            <div class="fw-action-group">
              <div class="fw-action-group__title">Help</div>
              <router-link class="fw-action fw-action--nested" to="/about" @click.native="closeMobileMenu">
                <span class="fw-action__icon"><i class="fa fa-info-circle"></i></span>
                <span class="fw-action__label">About ForeWarn</span>
                <i class="fa fa-chevron-right fw-action__chevron"></i>
              </router-link>
              <router-link class="fw-action fw-action--nested" to="/release-notes" @click.native="closeMobileMenu">
                <span class="fw-action__icon"><i class="fa fa-file-alt"></i></span>
                <span class="fw-action__label">Latest Release Note</span>
                <i class="fa fa-chevron-right fw-action__chevron"></i>
              </router-link>
            </div>
          </div>

          <button type="button" class="fw-logout" @click="logoutFromMobile">
            <i class="fa fa-sign-out-alt"></i>
            Sign Out
          </button>
        </div>
      </transition>
    </header>

    <div
        v-if="mobileMenuOpen"
        class="fw-backdrop d-lg-none"
        @click="closeMobileMenu"
        aria-hidden="true"
    ></div>

    <!--Vue Bootstrap Modal-->
    <b-modal ref="my-modal" hide-footer title="">
      <template v-slot:modal-title>
        <p style="font-weight: normal;font-size: 14px">You have logged in as <br> <span
            class="font-weight-bold">User Name :</span>
          {{ $store.state.firstName }} |
          <span class="font-weight-bold">Role :</span> {{ $store.state.roleName }} |
          <span class="font-weight-bold">since :</span>
          <span v-for="(x,idx) in roleName" v-bind:key="idx">{{ x.updateTimestamp }}</span>
        </p>
      </template>
      <div class="d-block text-center">
        <p>
          You have requested role switch from <span class="text-primary">{{ $store.state.roleName }}</span>
          to <span class="text-success">{{ switchedRole }}</span> <br>
          Do you like to make the role switch? (Y/N)
        </p>
      </div>
      <b-button class="mt-3 btn-danger btn-sm float-right" btn-sm @click="hideModal" v-b-tooltip.hover
                title="Cancel">
        <i class="fa fa-window-close"></i>
      </b-button>
      <b-button class="mt-3 btn-success btn-sm float-right" btn-sm @click="updateRole(rId)" v-b-tooltip.hover
                title="Proceed">
        <i class="fa fa-check"></i>
      </b-button>
    </b-modal>
  </div>
</template>
<script>
import axios from 'axios'

export default {
  name: 'AppHeader',
  components: {},
  data: function () {
    return {
      level1Menu: [],
      level2Menu: [],
      topMenu: [],
      menuMaster: {},
      fwMClient: {},
      userInfo: {},
      user: '',
      sideMenu: [],
      level1MenuFlag: false,
      level2MenuFlag: false,
      roleName: [],
      switchedRole: '',
      rId: '',
      logoPath: null,
      mobileMenuOpen: false,
      desktopMenu: null
    }
  },
  mounted: function () {
    this.getMenuMaster()
    this.getLogoPath()
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('click', this.handleDocumentClick)
    document.body.classList.remove('fw-header-menu-open')
  },
  computed: {
    showBanner() {
      return this.$store.state.showBanner
    },
    showSideMenu() {
      return this.$store.state.showSideMenu
    },
    isLoggedIn() {
      return this.$store.state.isLoggedIn
    },
    roleSwitchConfirmation() {
      return this.$store.state.roleSwitchConfirmation
    },
    userInitials() {
      const name = (this.$store.state.firstName || 'U').trim()
      const parts = name.split(/\s+/).filter(Boolean)
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.slice(0, 2).toUpperCase()
    },
    homeIconUrl() {
      let url;

      const {homeDashboardMenuId} = this.$store.state.user;
      const roleLevelMenuId = this.$store.state.userRoleRestriction.homeDashboardMenuId;

      if (!homeDashboardMenuId) {
        url = roleLevelMenuId || '/';
      } else {
        url = homeDashboardMenuId || '/';
      }

      if (!url.includes('/')) {
        url = '/'
      }
      return url
    }
  },
  watch: {
    mobileMenuOpen(open) {
      if (open) {
        document.body.classList.add('fw-header-menu-open')
      } else {
        document.body.classList.remove('fw-header-menu-open')
      }
    },
    '$route'() {
      this.closeMobileMenu()
      this.closeDesktopMenu()
    }
  },
  created() {
    this.loadRoleData()
    this.getLogoPath()
  },
  methods: {
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false
    },
    toggleDesktopMenu(menu) {
      this.desktopMenu = this.desktopMenu === menu ? null : menu
    },
    closeDesktopMenu() {
      this.desktopMenu = null
    },
    handleDocumentClick() {
      this.closeDesktopMenu()
    },
    handleResize() {
      if (window.innerWidth >= 992 && this.mobileMenuOpen) {
        this.closeMobileMenu()
      }
      if (window.innerWidth < 992) {
        this.closeDesktopMenu()
      }
    },
    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.mobileMenuOpen) this.closeMobileMenu()
        if (this.desktopMenu) this.closeDesktopMenu()
      }
    },
    toggleBannerFromMobile() {
      this.toggleBanner()
    },
    logoutFromMobile() {
      this.closeMobileMenu()
      this.logout()
    },
    myFunction: function () {
      document.getElementById('myDropdown').classList.toggle('show')
    },
    windowonclick: function (event) {
      if (!event.target.matches('.dropbtn')) {
        var dropdowns = document.getElementsByClassName('dropdown-content')
        var i
        for (i = 0; i < dropdowns.length; i++) {
          var openDropdown = dropdowns[i]
          if (openDropdown.classList.contains('show')) {
            openDropdown.classList.remove('show')
          }
        }
      }
    },
    logout: function () {
      this.$router.push('/logout')
    },
    updateRole: function () {
      axios.post('/auth/session').then(() => {
        this.saveRole()
      }, error => {
        console.error('Error in updating logout session', error)
      })
      this.$refs['my-modal'].hide()
      this.$store.commit('roleSwitchConfirmation')
    },
    saveRole: function () {
      console.log("Calling rolesession API after logout")
      axios.post(`/auth/rolesession/`, this.rId)
          .then(() => {
            this.roleUpdate()
          }, err => {
            console.error('Error in Inserting Record', err)
          })
    },
    roleUpdate: function () {
      let uId = this.$store.state.user.userRoles.userId
      axios.put('/auth/roleFlag/' + this.rId + '/' + uId)
          .then((res) => {
            this.handleRole(res.data)
          }, err => {
            console.error('Error in Updating Record', err)
          })
    },
    handleRole: function () {
      this.$store.dispatch('checkUserLogin')
    },
    showModal(roleName, roleId) {
      this.switchedRole = roleName
      this.rId = roleId
      this.$refs['my-modal'].show()
    },
    hideModal() {
      this.$refs['my-modal'].hide()
    },
    loadRoleData: function () {
      let uId = this.$store.state.user.userRoles.userId
      let url = '/api/roleSwitchData/' + uId

      axios.get(url)
          .then(res => {
            this.roleSwitchD = res.data

            let roleSelected = this.$store.state.user.userRoles.roleId

            if (Array.isArray(this.roleSwitchD)) {
              this.roleName = this.roleSwitchD.filter(r => r.roleId != roleSelected)
            } else {
              console.error("API did not return an array:", this.roleSwitchD)
            }

          })
          .catch(err => {
            console.error('Error in fetching record', err)
          })
    },
    toggleBanner: function () {
      this.$store.commit('toggleBanner', this.showBanner)
    },

    toggleSideMenu: function () {
      this.$store.commit('toggleSideMenu')
    },
    showMenu: function () {
      let element = document.getElementById("menunav");
      element.classList.remove("d-none")
      element.classList.add("float-menu");
    },
    ifRoute: (what) => {
      return what === location.pathname
    },
    getMenuMaster: function () {
      this.$store.dispatch('loadState').then((cfg) => {
        this.menuMaster = cfg.menuMasterData
        this.topMenu = this.menuMaster.filter(m => m.parentMenuId === 'Parent')
        this.sideMenu = this.menuMaster.filter(m => m.parentMenuId === 'Root')
      })
    },
    getLogoPath: function () {
      this.$store.dispatch("loadState").then((cfg) => {
        this.fwMClient = cfg.fwMClient
        this.logoPath = "/img" + this.fwMClient[0].clientLogoPath['desktop']
      })
    }
  }
}

</script>
<style scoped>
.app-header {
  --fw-navy: #0b2745;
  --fw-navy-deep: #071a2f;
  --fw-accent: #3d7dd6;
  --fw-danger: #d64545;
  --fw-surface: #f4f7fb;
  --fw-text: #1a2b3c;
  --fw-muted: #6b7c8f;
  --fw-border: rgba(11, 39, 69, 0.08);
  position: sticky;
  top: 0;
  z-index: 1100;
}

.fw-header {
  position: relative;
  z-index: 1102;
  background:
      radial-gradient(120% 180% at 100% 0%, rgba(61, 125, 214, 0.28) 0%, transparent 55%),
      linear-gradient(135deg, var(--fw-navy-deep) 0%, var(--fw-navy) 55%, #12365c 100%);
  box-shadow: 0 8px 24px rgba(7, 26, 47, 0.28);
}

.fw-header__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 56px;
  padding: 8px 14px 8px 0;
}

.fw-brand {
  /* Matches AppMenu.vue baseMenuWidth — stays fixed when menu is resized */
  --fw-menu-base-width: 230px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 230px;
  width: 230px;
  max-width: 230px;
  min-width: 230px;
  padding: 0 10px 0 12px;
  text-decoration: none;
}

.fw-brand__logo {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 25px;
  object-fit: contain;
  object-position: left center;
}

.fw-brand__powered {
  display: block;
  width: 100%;
  margin-top: 3px;
  font-size: 10px;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.72);
  text-align: left;
  white-space: nowrap;
}

.fw-brand__powered a {
  padding: 0;
  text-decoration: none;
}

.fw-brand__powered .fore {
  color: #4785ee;
  font-style: normal;
  font-weight: 600;
}

.fw-brand__powered .warn {
  color: #e31717;
  font-style: normal;
  font-weight: 600;
}

.fw-nav {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
  height: 40px;
}

.fw-nav__menu {
  position: relative;
  display: flex;
  align-items: center;
  height: 36px;
}

/* One shared shell for icons, text, and role — same height/center */
.fw-nav__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 36px;
  margin: 0;
  padding: 0 12px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.92);
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
}

.fw-nav__btn--icon {
  width: 36px;
  min-width: 36px;
  padding: 0;
  font-size: 15px;
  font-weight: 400;
}

.fw-nav__btn--icon i {
  display: block;
  line-height: 1;
  font-size: 15px;
}

.fw-nav__btn--text {
  gap: 6px;
}

.fw-nav__btn--pill {
  gap: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  cursor: default;
}

.fw-nav__btn--pill i {
  font-size: 13px;
  line-height: 1;
}

.fw-nav__btn--badge {
  position: relative;
}

.fw-nav__btn--danger:hover,
.fw-nav__btn--danger:focus {
  background: rgba(214, 69, 69, 0.22);
  color: #ffd7d7;
}

.fw-nav__btn:hover,
.fw-nav__btn:focus,
.fw-nav__menu.open > .fw-nav__btn {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  text-decoration: none;
  outline: none;
}

.fw-nav__btn--pill:hover,
.fw-nav__btn--pill:focus {
  background: rgba(255, 255, 255, 0.1);
}

.fw-nav__btn:active {
  transform: scale(0.97);
}

.fw-nav__user-label {
  display: inline-block;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1;
}

.fw-nav__caret {
  font-size: 11px;
  line-height: 1;
}

.fw-nav__menu-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1200;
  min-width: 200px;
  padding: 6px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(7, 26, 47, 0.22);
  border: 1px solid rgba(11, 39, 69, 0.08);
}

.fw-nav__menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #1a2b3c;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  line-height: 1.2;
}

.fw-nav__menu-item i {
  width: 14px;
  color: #3d7dd6;
  text-align: center;
}

.fw-nav__menu-item:hover,
.fw-nav__menu-item:focus {
  background: rgba(11, 39, 69, 0.06);
  color: #0b2745;
  text-decoration: none;
}

.fw-badge {
  position: absolute;
  top: 2px;
  right: 0;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #e85d3b;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px var(--fw-navy);
}

.fw-badge--inline {
  position: static;
  margin-left: auto;
  margin-right: 8px;
  box-shadow: none;
}

.fw-burger {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
}

.fw-burger:hover,
.fw-burger:focus {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.3);
  outline: none;
}

.fw-burger:active {
  transform: scale(0.96);
}

.fw-burger__line {
  display: block;
  width: 18px;
  height: 2px;
  border-radius: 2px;
  background: #fff;
  transition: transform 0.25s ease, opacity 0.2s ease, width 0.2s ease;
  transform-origin: center;
}

.fw-burger__line.open:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.fw-burger__line.open:nth-child(2) {
  opacity: 0;
  width: 0;
}

.fw-burger__line.open:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.fw-mobile-panel {
  position: absolute;
  top: calc(100% - 2px);
  left: 10px;
  right: 10px;
  max-height: min(78vh, 560px);
  overflow: auto;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, #f7fafc 100%);
  box-shadow:
      0 18px 40px rgba(7, 26, 47, 0.28),
      0 2px 0 rgba(255, 255, 255, 0.6) inset;
  color: var(--fw-text);
  -webkit-overflow-scrolling: touch;
}

.fw-mobile-panel__user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 14px;
  background:
      linear-gradient(135deg, rgba(11, 39, 69, 0.06), rgba(61, 125, 214, 0.1));
  border: 1px solid var(--fw-border);
}

.fw-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(145deg, #12365c, #3d7dd6);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  box-shadow: 0 8px 16px rgba(61, 125, 214, 0.28);
  flex-shrink: 0;
}

.fw-mobile-panel__name {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--fw-navy);
}

.fw-mobile-panel__role {
  margin-top: 4px;
  font-size: 12px;
  color: var(--fw-muted);
  display: flex;
  align-items: center;
  gap: 6px;
}

.fw-mobile-panel__actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fw-action {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--fw-text);
  text-align: left;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.18s ease, transform 0.15s ease;
  cursor: pointer;
}

.fw-action:hover,
.fw-action:focus {
  background: rgba(11, 39, 69, 0.06);
  color: var(--fw-navy);
  text-decoration: none;
  outline: none;
}

.fw-action:active {
  transform: scale(0.99);
}

.fw-action__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(11, 39, 69, 0.07);
  color: var(--fw-navy);
  flex-shrink: 0;
}

.fw-action__label {
  flex: 1;
}

.fw-action__chevron {
  color: #9aadc0;
  font-size: 11px;
}

.fw-action-group {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid var(--fw-border);
}

.fw-action-group__title {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fw-muted);
}

.fw-action--nested {
  padding-left: 12px;
}

.fw-logout {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 14px;
  padding: 13px 16px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #c93b3b 0%, var(--fw-danger) 55%, #b83333 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 10px 20px rgba(214, 69, 69, 0.28);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.fw-logout:hover,
.fw-logout:focus {
  filter: brightness(1.05);
  outline: none;
}

.fw-logout:active {
  transform: scale(0.985);
  box-shadow: 0 6px 14px rgba(214, 69, 69, 0.24);
}

.fw-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1101;
  background: rgba(7, 26, 47, 0.42);
  backdrop-filter: blur(2px);
  animation: fw-fade-in 0.2s ease;
}

.fw-panel-enter-active,
.fw-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.22s ease;
}

.fw-panel-enter,
.fw-panel-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}

@keyframes fw-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (max-width: 991.98px) {
  .fw-header__bar {
    min-height: 52px;
    padding: 8px 12px;
  }

  .fw-brand {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    max-width: none;
    padding: 0;
  }

  .fw-brand__logo {
    width: 150px;
    max-width: 42vw;
  }
}
</style>

<style>
/* Prevent background scroll while mobile menu is open */
body.fw-header-menu-open {
  overflow: hidden;
}
</style>
